// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import { VeraPromptProposal } from '../../models/admin';
import { AdminChatAssistResult } from '../../models/admin/AdminChatAssistResult';

export const adminBackendServiceVeraAdminAssistMixin = {
  getVeraAdminChatAssistPrompt(): Promise<{
    productionMarkdown: string;
    sandboxMarkdown: string;
    sandboxSource: 'default' | 'custom';
  }> {
    return this.authApiGetAuthed(
      'vera/admin-chat-assist/prompt',
      'Network error while fetching Vera admin chat assist prompt',
      (json) => {
        const data = json?.data ?? {};
        return {
          productionMarkdown: data.productionMarkdown ?? '',
          sandboxMarkdown: data.sandboxMarkdown ?? data.productionMarkdown ?? '',
          sandboxSource: data.sandboxSource ?? 'default',
        };
      },
    );
  },

  saveVeraAdminChatAssistPromptSandbox(markdown: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'vera/admin-chat-assist/prompt',
      'PUT',
      { markdown },
      () => undefined,
    );
  },

  getVeraAdminChatAssistIntentPrompt(): Promise<{
    productionText: string;
    sandboxText: string;
    sandboxSource: 'default' | 'custom';
  }> {
    return this.authApiGetAuthed(
      'vera/admin-chat-assist/prompt/intent',
      'Network error while fetching Vera admin chat assist intent prompt',
      (json) => {
        const data = json?.data ?? {};
        return {
          productionText: data.productionText ?? '',
          sandboxText: data.sandboxText ?? data.productionText ?? '',
          sandboxSource: data.sandboxSource ?? 'default',
        };
      },
    );
  },

  saveVeraAdminChatAssistIntentPrompt(text: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'vera/admin-chat-assist/prompt/intent',
      'PUT',
      { text },
      () => undefined,
    );
  },

  getVeraAdminChatAssistGroundingPrompt(): Promise<{ productionText: string }> {
    return this.authApiGetAuthed(
      'vera/admin-chat-assist/prompt/grounding',
      'Network error while fetching Vera admin chat assist grounding prompt',
      (json) => ({ productionText: json?.data?.productionText ?? '' }),
    );
  },

  saveVeraAdminAssistPreviewGrounding(sessionId: string, text: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `vera/admin-chat-assist/prompt/preview-session/${sessionId}/grounding`,
      'PUT',
      { text },
      () => undefined,
    );
  },

  saveVeraAdminAssistPreviewIntent(sessionId: string, text: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `vera/admin-chat-assist/prompt/preview-session/${sessionId}/intent`,
      'PUT',
      { text },
      () => undefined,
    );
  },

  getVeraAdminAssistUploadUrl(
    purpose:
      | 'preview-prompt'
      | 'admin-assist-proposal'
      | 'admin-assist-intent-proposal'
      | 'admin-assist-grounding-proposal',
    sessionId?: string,
  ): Promise<{ uploadUrl: string; gcsPath: string }> {
    const params = new URLSearchParams({ purpose });
    if (sessionId) params.set('sessionId', sessionId);
    return this.authApiGetAuthed(
      `vera/admin-chat-assist/prompt/upload-url?${params.toString()}`,
      'Network error while fetching Vera admin assist upload URL',
      (json) => json,
    );
  },

  saveVeraAdminAssistPreviewPrompt(sessionId: string, markdown: string): Promise<void> {
    return this.getVeraAdminAssistUploadUrl('preview-prompt', sessionId).then(({ uploadUrl }) =>
      this.uploadTextToGcs(uploadUrl, markdown),
    );
  },

  saveVeraAdminAssistPreviewTranscript(
    sessionId: string,
    transcript: Array<{
      id: string;
      senderRole: 'investor' | 'admin';
      senderName: string;
      text: string;
      timestamp: string;
    }>,
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `vera/admin-chat-assist/prompt/preview-session/${sessionId}/transcript`,
      'PUT',
      { transcript },
      () => undefined,
    );
  },

  previewVeraAdminAssist(
    contactExternalId: string,
    sessionId: string,
  ): Promise<AdminChatAssistResult> {
    return this.authApiMutateAuthed(
      'vera/admin-chat-assist/prompt/preview-assist',
      'POST',
      { contactExternalId, sessionId },
      (json) => json?.data,
    );
  },

  submitVeraAdminAssistProposal(
    markdown: string,
    sessionId?: string | null,
    notes?: string | null,
    intentText?: string | null,
    groundingText?: string | null,
  ): Promise<VeraPromptProposal> {
    const coachingUpload = this.getVeraAdminAssistUploadUrl('admin-assist-proposal').then(
      ({ uploadUrl, gcsPath }) => this.uploadTextToGcs(uploadUrl, markdown).then(() => gcsPath),
    );

    const intentUpload = intentText?.trim()
      ? this.getVeraAdminAssistUploadUrl('admin-assist-intent-proposal').then(
          ({ uploadUrl, gcsPath }) =>
            this.uploadTextToGcs(uploadUrl, intentText.trim()).then(() => gcsPath),
        )
      : Promise.resolve<string | null>(null);

    const groundingUpload = groundingText?.trim()
      ? this.getVeraAdminAssistUploadUrl('admin-assist-grounding-proposal').then(
          ({ uploadUrl, gcsPath }) =>
            this.uploadTextToGcs(uploadUrl, groundingText.trim()).then(() => gcsPath),
        )
      : Promise.resolve<string | null>(null);

    return Promise.all([coachingUpload, intentUpload, groundingUpload]).then(
      ([gcsPath, intentGcsPath, groundingGcsPath]) => {
        const body: {
          gcsPath: string;
          intentGcsPath?: string;
          groundingGcsPath?: string;
          sessionId?: string;
          notes?: string;
        } = { gcsPath };
        if (intentGcsPath) body.intentGcsPath = intentGcsPath;
        if (groundingGcsPath) body.groundingGcsPath = groundingGcsPath;
        if (sessionId) body.sessionId = sessionId;
        if (notes) body.notes = notes;
        return this.authApiMutateAuthed(
          'vera/admin-chat-assist/prompt/proposals',
          'POST',
          body,
          (json) => json?.data,
        );
      },
    );
  },
} satisfies ThisType<BaseBackendService>;
